import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, AreaChart, Area
} from 'recharts';
import {
  TrendingUp, Users, Package, Eye, Calendar, ArrowLeft,
  RefreshCw, UserCheck, AlertTriangle, CheckCircle,
  Clock, XCircle, MessageCircle, Phone, Download, Search, Filter
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── helpers ────────────────────────────────────────────────────────────────

const daysLeft = (isoDate) =>
  Math.ceil((new Date(isoDate) - Date.now()) / 86_400_000);

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
      })
    : '—';

const buildWAMessage = (vehicle, days) => {
  const regNo = vehicle.registration_no || vehicle.reg || '—';
  const type  = (vehicle.vehicle_type || vehicle.type || 'vehicle').toUpperCase();
  const brandModel =
    vehicle.brand_name && vehicle.model_name
      ? ` ${vehicle.brand_name} ${vehicle.model_name}`
      : '';

  let expiryNote;
  if (days <= 0)        expiryNote = 'has already expired';
  else if (days <= 3)   expiryNote = `is expiring in just ${days} day${days === 1 ? '' : 's'}`;
  else if (days <= 7)   expiryNote = `is expiring in ${days} days`;
  else                  expiryNote = `is expiring in ${days} days`;

  return encodeURIComponent(
    `Hi, Greetings from X7 Autoz! 🚗\n\n` +
    `Your${brandModel} vehicle with registration number *${regNo}* (${type}) ` +
    `listed on our portal ${expiryNote}.\n\n` +
    `Please make the payment and re-register your vehicle on the X7 Autoz portal ` +
    `to keep your listing active and visible to buyers.\n\n` +
    `For any assistance, feel free to reach out to us.\n\n` +
    `Thank you,\nTeam X7 Autoz`
  );
};

const downloadCSV = (data, filename) => {
  if (!data?.length) { toast.error('No data to download'); return; }
  const headers = Object.keys(data[0]);
  const rows = data.map(row =>
    headers.map(h => `"${(row[h] ?? '').toString().replace(/"/g, '""')}"`).join(',')
  );
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// ─── sub-components ─────────────────────────────────────────────────────────

const TypeBadge = ({ type }) => {
  const map = {
    car:        { bg: '#E6F1FB', color: '#042C53', label: 'Car' },
    bike:       { bg: '#EAF3DE', color: '#173404', label: 'Bike' },
    ev:         { bg: '#EEEDFE', color: '#26215C', label: 'EV' },
    commercial: { bg: '#FAEEDA', color: '#412402', label: 'Commercial' },
  };
  const s = map[type] || { bg: '#F1EFE8', color: '#2C2C2A', label: type };
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontSize: 10, padding: '2px 8px',
      borderRadius: 99, fontWeight: 500, whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
};

const FuelBadge = ({ fuel }) => (
  <span style={{
    background: 'var(--color-background-secondary)',
    color: 'var(--color-text-secondary)',
    fontSize: 10, padding: '2px 7px', borderRadius: 99,
  }}>
    {fuel || '—'}
  </span>
);

const DaysLeftBadge = ({ days }) => {
  if (days <= 0)      return <span style={{ color: '#E24B4A', fontWeight: 600, fontSize: 12 }}>Expired</span>;
  if (days <= 3)      return <span style={{ color: '#E24B4A', fontWeight: 600, fontSize: 12 }}>{days}d left</span>;
  if (days <= 7)      return <span style={{ color: '#D85A30', fontWeight: 600, fontSize: 12 }}>{days}d left</span>;
  return               <span style={{ color: '#BA7517',  fontWeight: 600, fontSize: 12 }}>{days}d left</span>;
};

// WhatsApp send button
const WAButton = ({ phone, message }) => {
  const clean = (phone || '').replace(/\D/g, '');
  const number = clean.startsWith('91') ? clean : `91${clean}`;
  return (
    <a
      href={`https://wa.me/${number}?text=${message}`}
      target="_blank"
      rel="noreferrer"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '5px 10px', borderRadius: 8,
        border: '0.5px solid #25D366', background: '#e8faf0',
        color: '#1a7a3a', fontSize: 11, fontWeight: 500,
        textDecoration: 'none', whiteSpace: 'nowrap',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="11" fill="#25D366"/>
        <path d="M17.5 14.4c-.3-.1-1.7-.8-1.9-.9-.3-.1-.5-.1-.7.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.2-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.2-.5 0-.2-.8-2-.9-2.3-.2-.4-.5-.3-.7-.3H7.8c-.2 0-.5.1-.7.3-.6.7-.9 1.6-.9 2.6 0 1 .7 2 .8 2.2.1.2 1.8 2.8 4.4 3.9 2.6 1.1 2.6.7 3.1.7.5 0 1.7-.7 1.9-1.3.2-.6.2-1.2.1-1.3-.1-.1-.3-.2-.6-.3z" fill="#fff"/>
      </svg>
      Send
    </a>
  );
};

// Expiry table
const ExpiryTable = ({ vehicles, days, searchQuery }) => {
  const filtered = vehicles.filter(v => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (v.registration_no || v.reg || '').toLowerCase().includes(q) ||
      (v.seller_name || v.owner || '').toLowerCase().includes(q) ||
      (v.vehicle_type || v.type || '').toLowerCase().includes(q) ||
      (v.city || '').toLowerCase().includes(q)
    );
  });

  if (!filtered.length) {
    return (
      <div className="text-center py-12 text-gray-400">
        <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-30" />
        <p>No vehicles found.</p>
      </div>
    );
  }

  const csvData = filtered.map(v => ({
    'Registration No': v.registration_no || v.reg || '',
    'Owner Name': v.seller_name || v.owner || '',
    'Vehicle Type': v.vehicle_type || v.type || '',
    'Fuel Type': v.fuel_type || v.fuel || '',
    'Contact': v.seller_phone || v.contact_phone || v.phone || '',
    'City': v.city || '',
    'Brand': v.brand_name || '',
    'Model': v.model_name || '',
    'Expires On': fmtDate(v.expires_at),
    'Days Left': daysLeft(v.expires_at),
  }));

  return (
    <div>
      <div className="flex justify-end mb-2">
        <Button
          variant="outline" size="sm"
          onClick={() => downloadCSV(csvData, `expiry_${days}days.csv`)}
        >
          <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
        </Button>
      </div>
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left p-3 text-gray-600 font-medium" style={{ width: '8%' }}>Type</th>
              <th className="text-left p-3 text-gray-600 font-medium" style={{ width: '15%' }}>Owner name</th>
              <th className="text-left p-3 text-gray-600 font-medium" style={{ width: '14%' }}>Reg number</th>
              <th className="text-left p-3 text-gray-600 font-medium" style={{ width: '10%' }}>Fuel</th>
              <th className="text-left p-3 text-gray-600 font-medium" style={{ width: '12%' }}>Contact</th>
              <th className="text-left p-3 text-gray-600 font-medium" style={{ width: '9%' }}>City</th>
              <th className="text-left p-3 text-gray-600 font-medium" style={{ width: '12%' }}>Expires on</th>
              <th className="text-left p-3 text-gray-600 font-medium" style={{ width: '9%' }}>Days left</th>
              <th className="text-left p-3 text-gray-600 font-medium" style={{ width: '11%' }}>WhatsApp</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((v, i) => {
              const phone   = v.seller_phone || v.contact_phone || v.phone || '';
              const dl      = daysLeft(v.expires_at);
              const waMsg   = buildWAMessage(v, dl);
              const regNo   = v.registration_no || v.reg || '—';
              const owner   = v.seller_name || v.owner || '—';
              const fuel    = v.fuel_type || v.fuel || '—';
              const vtype   = v.vehicle_type || v.type || '';

              return (
                <tr key={v.id || i} className="border-b hover:bg-gray-50">
                  <td className="p-3"><TypeBadge type={vtype} /></td>
                  <td className="p-3 font-medium truncate max-w-0" title={owner}>{owner}</td>
                  <td className="p-3 font-mono text-xs">{regNo}</td>
                  <td className="p-3"><FuelBadge fuel={fuel} /></td>
                  <td className="p-3 text-xs">
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3 text-gray-400 flex-shrink-0" />
                      {phone || '—'}
                    </div>
                  </td>
                  <td className="p-3 text-xs text-gray-600">{v.city || '—'}</td>
                  <td className="p-3 text-xs text-gray-500">{fmtDate(v.expires_at)}</td>
                  <td className="p-3"><DaysLeftBadge days={dl} /></td>
                  <td className="p-3">
                    {phone
                      ? <WAButton phone={phone} message={waMsg} />
                      : <span className="text-xs text-gray-400">No phone</span>
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400 mt-2 text-right">{filtered.length} vehicle(s) shown</p>
    </div>
  );
};

// Mini stat card
const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
  <Card data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-secondary-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-secondary-800">{value.toLocaleString()}</p>
          {subtitle && <p className="text-sm text-secondary-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ─── main component ──────────────────────────────────────────────────────────

const AnalyticsDashboard = () => {
  const navigate  = useNavigate();
  const { token, user } = useAuth();

  const [selectedCity, setSelectedCity] = useState('Belgaum');
  const [cities, setCities]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [expiryLoading, setExpiryLoading] = useState(false);

  const [analytics, setAnalytics] = useState({
    total_vehicles: 0, pending_approvals: 0, approved_vehicles: 0,
    total_users: 0, total_agents: 0, total_views: 0,
    vehicles_by_type: [], vehicles_by_city: [], top_brands: [], monthly_trends: [],
  });

  // Expiry analytics state
  const [expiryData, setExpiryData] = useState({
    expiring15: [], expiring7: [], expiring3: [], expired: [],
    soldCount: 0, renewedCount: 0, activeCount: 0,
  });

  const [expiryTab,    setExpiryTab]    = useState('exp15');
  const [expirySearch, setExpirySearch] = useState('');

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // ── fetch base analytics ──────────────────────────────────────────────────
  const fetchCities = async () => {
    try { const r = await axios.get(`${API}/cities`); setCities(r.data.cities); } catch {}
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/analytics`, authHeaders);
      setAnalytics(r.data);
    } catch { toast.error('Failed to load analytics data'); }
    finally { setLoading(false); }
  };

  // ── fetch expiry analytics ────────────────────────────────────────────────
  const fetchExpiryAnalytics = useCallback(async () => {
    setExpiryLoading(true);
    try {
      // Try the dedicated endpoint first; fall back to full vehicle list
      let vehicles = [];
      try {
        const r = await axios.get(`${API}/superadmin/vehicle-expiry-analytics`, authHeaders);
        vehicles = r.data.vehicles || [];
      } catch {
        // Fallback: pull all approved vehicles and filter client-side
        const r = await axios.get(
          `${API}/admin/vehicles?vehicle_status=approved&limit=2000`,
          authHeaders
        );
        vehicles = r.data.vehicles || [];
      }

      // Enrich brand/model if missing (they may already be there from the endpoint)
      // Separate sold from non-sold
      const nonSold  = vehicles.filter(v => v.status !== 'sold');
      const sold     = vehicles.filter(v => v.status === 'sold');
      const exp15    = nonSold.filter(v => v.expires_at && daysLeft(v.expires_at) > 0  && daysLeft(v.expires_at) <= 15);
      const exp7     = nonSold.filter(v => v.expires_at && daysLeft(v.expires_at) > 0  && daysLeft(v.expires_at) <= 7);
      const exp3     = nonSold.filter(v => v.expires_at && daysLeft(v.expires_at) > 0  && daysLeft(v.expires_at) <= 3);
      const expired  = nonSold.filter(v => v.expires_at && daysLeft(v.expires_at) <= 0);
      const active   = nonSold.filter(v => v.expires_at && daysLeft(v.expires_at) > 15);

      // Renewed = vehicles that have been re-paid (payment_status paid, status approved,
      // and were previously expired). As a simple proxy: vehicles with payment_type = 'renewal'
      // We'll count them from the vehicles list if that field exists.
      const renewed = nonSold.filter(v => v.type === 'renewal' || v.payment_type === 'renewal');

      setExpiryData({
        expiring15: exp15,
        expiring7:  exp7,
        expiring3:  exp3,
        expired,
        soldCount:    sold.length,
        renewedCount: renewed.length,
        activeCount:  active.length,
      });
    } catch (err) {
      toast.error('Failed to load expiry analytics');
    } finally {
      setExpiryLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCities();
    fetchAnalytics();
    fetchExpiryAnalytics();
  }, []);

  const COLORS = ['#F97316', '#1F2937', '#6B7280', '#EA580C', '#9CA3AF', '#3B82F6', '#10B981'];

  const formatMonthLabel = (monthStr) => {
    if (!monthStr) return '';
    const [, month] = monthStr.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return months[parseInt(month) - 1] || monthStr;
  };

  // ── pie chart data (vehicle status overview) ──────────────────────────────
  const { expiring15, expiring7, expiring3, expired, soldCount, renewedCount, activeCount } = expiryData;

  const totalTracked = activeCount + renewedCount + expired.length + soldCount;
  const pieStatusData = [
    { name: 'Active',   value: activeCount,      color: '#639922' },
    { name: 'Renewed',  value: renewedCount,      color: '#378ADD' },
    { name: 'Expired',  value: expired.length,    color: '#E24B4A' },
    { name: 'Sold',     value: soldCount,          color: '#7F77DD' },
  ];

  // Expiry breakdown bar data (relative widths)
  const maxExpCount = Math.max(expiring15.length, expiring7.length, expiring3.length, expired.length, 1);
  const expiryBreakdown = [
    { label: 'Within 3 days',  count: expiring3.length,  color: '#E24B4A' },
    { label: 'Within 7 days',  count: expiring7.length,  color: '#D85A30' },
    { label: 'Within 15 days', count: expiring15.length, color: '#BA7517' },
    { label: 'Already expired',count: expired.length,    color: '#888780' },
  ];

  const EXPIRY_TABS = [
    {
      key: 'exp15', label: 'Expiring in 15 days', count: expiring15.length,
      data: expiring15, days: 15,
      badgeBg: '#FAEEDA', badgeColor: '#633806',
      icon: <Clock className="h-4 w-4" />,
    },
    {
      key: 'exp7',  label: 'Expiring in 7 days',  count: expiring7.length,
      data: expiring7,  days: 7,
      badgeBg: '#FAECE7', badgeColor: '#4A1B0C',
      icon: <AlertTriangle className="h-4 w-4" />,
    },
    {
      key: 'exp3',  label: 'Expiring in 3 days',  count: expiring3.length,
      data: expiring3,  days: 3,
      badgeBg: '#FCEBEB', badgeColor: '#501313',
      icon: <XCircle className="h-4 w-4" />,
    },
    {
      key: 'expired', label: 'Expired & not renewed', count: expired.length,
      data: expired,  days: 0,
      badgeBg: '#F1EFE8', badgeColor: '#2C2C2A',
      icon: <XCircle className="h-4 w-4" />,
    },
  ];

  const activeTabObj = EXPIRY_TABS.find(t => t.key === expiryTab) || EXPIRY_TABS[0];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-secondary-50">
      <Header cities={cities} selectedCity={selectedCity} setSelectedCity={setSelectedCity} />

      <div className="px-4 md:px-8 lg:px-12 py-8">

        {/* ── Page header ─────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-secondary-800 mb-1">Analytics Dashboard</h1>
              <p className="text-secondary-600">
                {user?.role === 'superadmin'
                  ? 'Platform-wide insights and statistics'
                  : 'City-specific insights and statistics'}
              </p>
            </div>
          </div>
          <Button
            onClick={() => { fetchAnalytics(); fetchExpiryAnalytics(); }}
            disabled={loading}
            variant="outline"
            className="gap-2"
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* ── General stat cards ──────────────────────────────────────────── */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
          <StatCard icon={Package}    title="Total Vehicles" value={analytics.total_vehicles}    color="bg-primary-100 text-primary-600" />
          <StatCard icon={Eye}        title="Total Views"    value={analytics.total_views}        color="bg-blue-100 text-blue-600" />
          <StatCard icon={Users}      title="Total Users"    value={analytics.total_users}        color="bg-green-100 text-green-600" />
          <StatCard icon={UserCheck}  title="Active Agents"  value={analytics.total_agents}       color="bg-purple-100 text-purple-600" />
          <StatCard icon={Calendar}   title="Pending"        value={analytics.pending_approvals}  color="bg-yellow-100 text-yellow-600" />
          <StatCard icon={TrendingUp} title="Approved"       value={analytics.approved_vehicles}  color="bg-emerald-100 text-emerald-600" />
        </div>

        {/* ── Standard charts row ──────────────────────────────────────────── */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader><CardTitle>Monthly Trends</CardTitle></CardHeader>
            <CardContent>
              {analytics.monthly_trends.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics.monthly_trends}>
                    <defs>
                      <linearGradient id="colorListings" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F97316" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#F97316" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1F2937" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#1F2937" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="month" tickFormatter={formatMonthLabel} stroke="#6B7280" />
                    <YAxis stroke="#6B7280" />
                    <Tooltip labelFormatter={formatMonthLabel} contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                    <Legend />
                    <Area type="monotone" dataKey="listings" stroke="#F97316" fillOpacity={1} fill="url(#colorListings)" strokeWidth={2} />
                    <Area type="monotone" dataKey="views"    stroke="#1F2937" fillOpacity={1} fill="url(#colorViews)"    strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-secondary-500">No trend data available yet</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Vehicles by Type</CardTitle></CardHeader>
            <CardContent>
              {analytics.vehicles_by_type.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={analytics.vehicles_by_type} cx="50%" cy="50%" labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100} dataKey="value">
                      {analytics.vehicles_by_type.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-secondary-500">No vehicle type data available</div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader><CardTitle>Vehicles by City</CardTitle></CardHeader>
            <CardContent>
              {analytics.vehicles_by_city.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.vehicles_by_city}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="city" stroke="#6B7280" />
                    <YAxis stroke="#6B7280" />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                    <Bar dataKey="vehicles" fill="#F97316" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-secondary-500">No city data available</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Top Brands</CardTitle></CardHeader>
            <CardContent>
              {analytics.top_brands.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.top_brands} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis type="number" stroke="#6B7280" />
                    <YAxis dataKey="brand" type="category" width={100} stroke="#6B7280" />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                    <Bar dataKey="count" fill="#1F2937" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-secondary-500">No brand data available</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Key metrics summary ──────────────────────────────────────────── */}
        <Card className="mb-8">
          <CardHeader><CardTitle>Key Metrics Summary</CardTitle></CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { label: 'Approved Vehicles',      value: analytics.approved_vehicles, color: 'text-primary-500' },
                { label: 'Approval Rate',          value: `${analytics.total_vehicles > 0 ? ((analytics.approved_vehicles / analytics.total_vehicles) * 100).toFixed(0) : 0}%`, color: 'text-green-500' },
                { label: 'Avg. Views per Vehicle', value: analytics.total_vehicles > 0 ? (analytics.total_views / analytics.total_vehicles).toFixed(1) : 0, color: 'text-blue-500' },
                { label: 'Active Cities',          value: analytics.vehicles_by_city.length, color: 'text-purple-500' },
              ].map(m => (
                <div key={m.label} className="text-center p-4 bg-secondary-100 rounded-lg">
                  <div className={`text-2xl font-bold ${m.color}`}>{m.value}</div>
                  <div className="text-sm text-secondary-600 mt-1">{m.label}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ══════════════════════════════════════════════════════════════════
            VEHICLE EXPIRY ANALYTICS SECTION
        ══════════════════════════════════════════════════════════════════ */}
        <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-secondary-800">Vehicle Expiry Analytics</h2>
            <p className="text-sm text-secondary-500 mt-0.5">
              Listings nearing expiry — send renewal reminders via WhatsApp. Sold vehicles are excluded.
            </p>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={fetchExpiryAnalytics}
            disabled={expiryLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${expiryLoading ? 'animate-spin' : ''}`} />
            Refresh expiry data
          </Button>
        </div>

        {/* ── Expiry summary cards ─────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
          {[
            { label: 'Active',          value: activeCount,          bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200' },
            { label: 'Renewed',         value: renewedCount,          bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200'  },
            { label: 'Expiring ≤15d',   value: expiring15.length,     bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200' },
            { label: 'Expiring ≤7d',    value: expiring7.length,      bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
            { label: 'Expiring ≤3d',    value: expiring3.length,      bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200'   },
            { label: 'Expired',         value: expired.length,        bg: 'bg-gray-50',   text: 'text-gray-700',   border: 'border-gray-200'  },
            { label: 'Sold',            value: soldCount,              bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200'},
            { label: 'Total tracked',   value: totalTracked,           bg: 'bg-secondary-50', text: 'text-secondary-700', border: 'border-secondary-200' },
          ].map(s => (
            <Card key={s.label} className={`border ${s.border} ${s.bg}`}>
              <CardContent className="p-3">
                <p className="text-xs text-gray-500 mb-1">{s.label}</p>
                <p className={`text-2xl font-bold ${s.text}`}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ── Pie chart + Expiry breakdown ─────────────────────────────────── */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">

          {/* Status overview pie */}
          <Card>
            <CardHeader><CardTitle>Vehicle status overview</CardTitle></CardHeader>
            <CardContent>
              {expiryLoading ? (
                <div className="h-[280px] flex items-center justify-center">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={pieStatusData}
                        cx="50%" cy="50%"
                        innerRadius={55} outerRadius={90}
                        dataKey="value"
                        labelLine={false}
                        label={({ name, percent }) =>
                          percent > 0.03 ? `${name} ${(percent * 100).toFixed(0)}%` : ''
                        }
                      >
                        {pieStatusData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(val, name) => [val, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-x-5 gap-y-2 mt-2 justify-center">
                    {pieStatusData.map(d => (
                      <div key={d.name} className="flex items-center gap-1.5 text-xs text-gray-600">
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: d.color, display: 'inline-block', flexShrink: 0 }} />
                        {d.name}: <strong>{d.value}</strong>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Expiry breakdown bars */}
          <Card>
            <CardHeader><CardTitle>Expiry breakdown</CardTitle></CardHeader>
            <CardContent className="pt-2">
              {expiryLoading ? (
                <div className="h-[220px] flex items-center justify-center">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <div className="space-y-4 mt-2">
                  {expiryBreakdown.map(e => (
                    <div key={e.label} className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 w-36 flex-shrink-0">{e.label}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-2 rounded-full transition-all"
                          style={{
                            width: `${Math.round((e.count / maxExpCount) * 100)}%`,
                            background: e.color,
                          }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-gray-700 w-8 text-right">{e.count}</span>
                    </div>
                  ))}

                  {/* Quick info callout */}
                  {(expiring3.length > 0 || expired.length > 0) && (
                    <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-700">
                        {expiring3.length > 0 && (
                          <><strong>{expiring3.length}</strong> vehicle{expiring3.length > 1 ? 's' : ''} expiring in 3 days. </>
                        )}
                        {expired.length > 0 && (
                          <><strong>{expired.length}</strong> vehicle{expired.length > 1 ? 's' : ''} already expired and not renewed.</>
                        )}
                        {' '}Send WhatsApp reminders from the tables below.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Expiry tabs + tables ─────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle>Vehicles expiring soon / expired</CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={expirySearch}
                    onChange={e => setExpirySearch(e.target.value)}
                    placeholder="Search by name, reg, type, city..."
                    className="border rounded-lg pl-9 pr-4 py-2 text-sm w-64 focus:outline-none focus:ring-1 focus:ring-blue-300"
                  />
                </div>
              </div>

              {/* Tab bar */}
              <div className="flex flex-wrap gap-2">
                {EXPIRY_TABS.map(t => (
                  <button
                    key={t.key}
                    onClick={() => setExpiryTab(t.key)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 99,
                      border: expiryTab === t.key ? 'none' : '0.5px solid #e5e7eb',
                      background: expiryTab === t.key ? t.badgeBg : 'transparent',
                      color: expiryTab === t.key ? t.badgeColor : '#6b7280',
                      fontWeight: 500, fontSize: 12.5, cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    {t.label}
                    <span style={{
                      background: t.badgeBg, color: t.badgeColor,
                      fontSize: 10, fontWeight: 700,
                      padding: '1px 7px', borderRadius: 99,
                      border: `1px solid ${t.badgeColor}40`,
                    }}>
                      {t.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {expiryLoading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400 mb-3" />
                <p className="text-gray-400 text-sm">Loading expiry data...</p>
              </div>
            ) : (
              <ExpiryTable
                vehicles={activeTabObj.data}
                days={activeTabObj.days}
                searchQuery={expirySearch}
              />
            )}
          </CardContent>
        </Card>

      </div>
      <Footer />
    </div>
  );
};

export default AnalyticsDashboard;